import {
  STREET_SURVEY_PER_DIRECTION_LIMIT,
  STREET_SURVEY_TOTAL_LIMIT,
  type StreetSurveyDuplicateMode,
  type StreetSurveyExtractionDef,
  type StreetSurveyMasterDetail,
} from '@citizen-walk/shared-types';

/**
 * 机上調査「解析の設定（新方式）」の下書き ↔ 保存値の変換 (docs/27 §15-3、pure)。
 * 机上調査テンプレート (docs/27 §17)・出力フォーマット (移行期間) と地区の上書きの編集で使う。
 *
 * 入力欄は文字列で持ち、保存時に数値へ直して範囲を検査する。空欄・「引き継ぐ」は保存値では null にする
 * (地区上書きでは「テンプレートに従う」、テンプレートでは「既定」の意味。Firestore の merge 保存で
 * 前回値を確実に上書きするため undefined は使わない)。
 */

/** 3 択 (引き継ぐ / する / しない)。 */
export type InheritableSwitch = 'inherit' | 'on' | 'off';

export interface StreetSurveyExtractionDraft {
  enabled: boolean;
  /** '' = 引き継ぐ (自動)。 */
  anchorAxisKey: string;
  /** auto = 引き継ぐ (自動) / custom = 選んだ項目だけ。 */
  attachedMode: 'auto' | 'custom';
  attachedAxisKeys: string[];
  /** '' = 引き継ぐ (出力フォーマットの上限)。 */
  perDirectionText: string;
  /** '' = 引き継ぐ (絞らない)。 */
  totalText: string;
  /** '' = 引き継ぐ (既定の文)。 */
  selectionCriteria: string;
  /** 選抜プロンプトの冒頭の役割の文 (docs/27 §17)。'' = 引き継ぐ。 */
  selectionRole: string;
  /** エリアの要約の指示。'' = 引き継ぐ。 */
  areaSummaryInstruction: string;
  /** 地点の説明文の指示。'' = 引き継ぐ。 */
  descriptionInstruction: string;
  observationInstruction: string;
  evaluationGuidance: string;
  duplicateRule: string;
  /** '' = 引き継ぐ。 */
  duplicateMode: '' | StreetSurveyDuplicateMode;
  /** '' = 引き継ぐ。 */
  masterDetail: '' | StreetSurveyMasterDetail;
  includeMasterUsageRules: InheritableSwitch;
}

export function extractionDraftFromDef(
  def: StreetSurveyExtractionDef | null | undefined,
): StreetSurveyExtractionDraft {
  const d = def ?? {};
  return {
    enabled: d.enabled === true,
    anchorAxisKey: d.anchorAxisKey ?? '',
    attachedMode: Array.isArray(d.attachedAxisKeys) ? 'custom' : 'auto',
    attachedAxisKeys: Array.isArray(d.attachedAxisKeys) ? [...d.attachedAxisKeys] : [],
    perDirectionText:
      typeof d.perDirectionMaxItems === 'number' ? String(d.perDirectionMaxItems) : '',
    totalText: typeof d.totalMaxItems === 'number' ? String(d.totalMaxItems) : '',
    selectionCriteria: d.selectionCriteria ?? '',
    selectionRole: d.selectionRole ?? '',
    areaSummaryInstruction: d.areaSummaryInstruction ?? '',
    descriptionInstruction: d.descriptionInstruction ?? '',
    observationInstruction: d.observationInstruction ?? '',
    evaluationGuidance: d.evaluationGuidance ?? '',
    duplicateRule: d.duplicateRule ?? '',
    duplicateMode:
      d.duplicateMode === 'none' || d.duplicateMode === 'same-object' ? d.duplicateMode : '',
    masterDetail: d.masterDetail === 'compact' || d.masterDetail === 'full' ? d.masterDetail : '',
    includeMasterUsageRules:
      d.includeMasterUsageRules === true
        ? 'on'
        : d.includeMasterUsageRules === false
          ? 'off'
          : 'inherit',
  };
}

function parseLimit(
  text: string,
  range: { min: number; max: number },
  label: string,
  errors: string[],
): number | null {
  const t = text.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isInteger(n) || n < range.min || n > range.max) {
    errors.push(`${label}は ${range.min}〜${range.max} の整数で入力してください。`);
    return null;
  }
  return n;
}

export function extractionDefFromDraft(draft: StreetSurveyExtractionDraft): {
  def: StreetSurveyExtractionDef;
  errors: string[];
} {
  const errors: string[] = [];
  const def: StreetSurveyExtractionDef = {
    enabled: draft.enabled,
    anchorAxisKey: draft.anchorAxisKey || null,
    attachedAxisKeys: draft.attachedMode === 'custom' ? [...draft.attachedAxisKeys] : null,
    perDirectionMaxItems: parseLimit(
      draft.perDirectionText,
      STREET_SURVEY_PER_DIRECTION_LIMIT,
      '1 方向あたりの件数',
      errors,
    ),
    totalMaxItems: parseLimit(draft.totalText, STREET_SURVEY_TOTAL_LIMIT, '地点全体の件数', errors),
    selectionCriteria: draft.selectionCriteria.trim() || null,
    selectionRole: draft.selectionRole.trim() || null,
    areaSummaryInstruction: draft.areaSummaryInstruction.trim() || null,
    descriptionInstruction: draft.descriptionInstruction.trim() || null,
    observationInstruction: draft.observationInstruction.trim() || null,
    evaluationGuidance: draft.evaluationGuidance.trim() || null,
    duplicateRule: draft.duplicateRule.trim() || null,
    duplicateMode: draft.duplicateMode || null,
    masterDetail: draft.masterDetail || null,
    includeMasterUsageRules:
      draft.includeMasterUsageRules === 'inherit' ? null : draft.includeMasterUsageRules === 'on',
  };
  return { def, errors };
}
